/* eslint-disable consistent-return */
import React, { useState } from 'react';
import styled from 'styled-components';
import { useCopyToClipboard } from '@panel/commons/hooks/useCopyToClipboard';
import { useSelector } from 'react-redux';
import trendingUp from '@panel/commons/assets/icons/trending-up-darkgrey.svg';
import SimpleTooltip from '@panel/commons/lib/Tooltip/SimpleTooltip';
import trendingUpDisabled from '@panel/commons/assets/icons/trending-up-light.svg';
import { useTranslation } from 'react-i18next';
import { isPortalWithEcommerce, getIsTransitionOrBundleCredit } from '@panel/commons/helpers';
import { selectColor, sendTrackingInfo, ModalBase } from '@panel/commons';
import PerformanceTips from '@panel/commons/lib/Modals/PerformanceTips';
import { select } from '@panel/commons/core/themev2';
import PerformanceGreen from '@panel/commons/assets/icons/performance-green.svg';
import PerformanceYellow from '@panel/commons/assets/icons/performance-yellow.svg';
import PerformanceRed from '@panel/commons/assets/icons/performance-red.svg';
import PerformanceGray from '@panel/commons/assets/icons/performance-black_gris.svg';
import { creditsSelectors } from '../../../../ducks/credits';
import { CopyButton } from './styles';
import { Modal } from '../../Modals/ModalWrapper';
import UpgradePlanModal from '../../Modals/UpgradePlanModal';
import LeadsInfo from './LeadsInfo';

function MetricsSection({
  internalCode,
  postingId,
  dates,
  linkUpgradePlan,
  canUpgradePlan,
  showCopyFeedbackMessage,
  performanceStatus,
  daysLeftToQuality,
  linkToDesempeno,
  allowRedirectDesempeno,
  validations,
  publicationType,
  isDuplicate,
  isParticular,
  posting,
  showTips,
  setShowTips,
  isIntegrator,
  publisherTypeId,
  isAssistant
}) {
  const { t } = useTranslation();
  const { loading } = useSelector(creditsSelectors.getCreditsState);
  // eslint-disable-next-line no-unused-vars
  const [value, copy] = useCopyToClipboard();
  const { status } = dates;
  const isFinished = status === 'OFFLINE';
  const withoutCredit = status === 'WITHOUT_CREDIT';
  const [upgradePlanModal, setUpgradePlanModal] = useState(false);
  const operationType = posting.pricesOperationTypes[0] && posting.pricesOperationTypes[0].operationType;
  const planName = posting.publicationPlan ? t(`panel.avisos.planName.${posting.publicationPlan}`) : '';
  const isDevelopment = posting.publicationType && posting.publicationType !== 'Clasificado';
  const isOffline = ['DRAFT', 'OFFLINE', 'WITHOUT_CREDIT', 'DELETED'].includes(status);
  const noPerformance = isOffline || !performanceStatus || performanceStatus === 'NO-DATA-AVAILABLE';
  const disablePlanLink = !canUpgradePlan || isOffline || isDevelopment;
  const statusFiltered = noPerformance ? 'NO-DATA-AVAILABLE' : performanceStatus;
  const buttonTextDL = t(`panel.avisos.posting.CTA${statusFiltered === 'ALTO' ? '1' : '2'}`);
  // esta variable se crea ya que los usuarios particulares en LLVE no tienen disponible el ecommerce
  const isPortalWithoutEcommerce = isParticular && !isPortalWithEcommerce;
  const { publicationPlanId, publicationName } = posting;
  const isTransitionOrBundleCredit = getIsTransitionOrBundleCredit(publicationPlanId);

  const showDaysForPerformance =
    statusFiltered === 'NO-DATA-AVAILABLE' &&
    typeof daysLeftToQuality === 'number' &&
    daysLeftToQuality !== 0 &&
    daysLeftToQuality > 0 &&
    daysLeftToQuality < 30;

  const showCalculatingText = showDaysForPerformance ? '.calculating' : null;

  const getButtonText = () => {
    if (isOffline || (statusFiltered === 'NO-DATA-AVAILABLE' && daysLeftToQuality === 0)) {
      return '2';
    }
    if (statusFiltered === 'NO-DATA-AVAILABLE') {
      return '0';
    }
    if (statusFiltered === 'ALTO') {
      return '1';
    }
    return '2';
  };

  const tooltipComponent = (statusPerf, daysLeft) => (
    <TooltipComponent>
      {t(
        `panel.avisos.posting.quality.tooltip.NO-DATA-AVAILABLE${statusPerf}${
          typeof daysLeft === 'number' && daysLeft > 1 ? '.plural' : ''
        }`,
        {
          value: daysLeft
        }
      )}
    </TooltipComponent>
  );

  const calculateFillPercentage = daysLeft => {
    const maxDays = 29;
    if (typeof daysLeft !== 'number' || daysLeft <= 0 || daysLeft > maxDays) {
      return 0;
    }
    return ((maxDays - daysLeft) / maxDays) * 100;
  };

  return (
    <ContentLayout>
      {showTips && (
        <Modal>
          <ModalBase openModal={showTips} onClose={() => setShowTips(false)} width='328px' height='fit-content'>
            <PerformanceTips
              datalayerText='Card del aviso-Completitud del aviso_Ir a editar el aviso'
              tips={validations}
              postingId={postingId}
              isDuplicate={isDuplicate}
              device='mobile'
              isIntegrator={isIntegrator}
              isAssistant={isAssistant}
              publisherTypeId={publisherTypeId}
              publicationType={publicationType}
            />
          </ModalBase>
        </Modal>
      )}
      {upgradePlanModal && (
        <Modal>
          <ModalBase
            openModal={upgradePlanModal}
            onClose={() => setUpgradePlanModal(false)}
            width='328px'
            height='fit-content'
            isLoading={loading}
          >
            <UpgradePlanModal
              onClose={() => setUpgradePlanModal(false)}
              posting={posting}
              publicationType={publicationType}
              operationType={operationType}
              device='mobile'
              publisherId={posting.publisherId}
              isIntegrator={isIntegrator}
              publisherTypeId={publisherTypeId}
            />
          </ModalBase>
        </Modal>
      )}
      <>
        <div className='performance-container'>
          <PerformanceStatus status={noPerformance ? 'NO-DATA-AVAILABLE' : performanceStatus}>
            <span />
            <div data-panel='avisos-performanceStatus-metrics'>
              {t(`panel.aviso.desempeno.status.${noPerformance ? 'NO-DATA-AVAILABLE' : performanceStatus}`)}
            </div>
          </PerformanceStatus>
        </div>
        <SimpleTooltip
          backgroundColor={select('black')}
          offset={8}
          isMobile={!showDaysForPerformance}
          position='bottom-right'
          text={tooltipComponent(showCalculatingText, daysLeftToQuality)}
          dataPanel='infoToCta-improvePerformance'
          width='169px'
          height='49px'
        >
          <PerformanceLink
            href={!noPerformance && allowRedirectDesempeno ? linkToDesempeno : null}
            rel='noopener noreferrer'
            status={noPerformance ? 'NO-DATA-AVAILABLE' : performanceStatus}
            left
            onClick={() => {
              sendTrackingInfo('Panel', 'Mis avisos', `Card del aviso-${buttonTextDL}`);
            }}
            data-panel='avisos-performance-link'
          >
            <ContentWrapper
              status={statusFiltered}
              disabled={isOffline || (statusFiltered === 'NO-DATA-AVAILABLE' && daysLeftToQuality === 0)}
            >
              <div className='buttonText'>{t(`panel.avisos.posting.CTA${getButtonText()}`)}</div>
              {showDaysForPerformance && <DaysLeftBar fillValue={calculateFillPercentage(daysLeftToQuality)} />}
            </ContentWrapper>
          </PerformanceLink>
        </SimpleTooltip>
      </>
      <LeadsInfo isOnline={!isOffline} posting={posting} />
      <PostingInfo>
        <InfoItem data-panel='posting-code-container'>
          <span data-panel='posting-code-title'>{t('postings.card.detail.internalCode')}</span>
          <span data-panel='posting-code-value'>{internalCode || '-'}</span>
          <CopyButton
            onClick={() => {
              copy(internalCode);
              showCopyFeedbackMessage(t('panel.posting.copy.codigo.success'));
            }}
            data-panel='posting-copyCode-mobile'
          />
        </InfoItem>
        <InfoItem data-panel='posting-id-container'>
          <span data-panel='posting-id-title'>{t('postings.card.detail.postingId')}</span>
          <span data-panel='posting-id-value'>{postingId || '-'}</span>
          <CopyButton
            onClick={() => {
              copy(postingId);
              showCopyFeedbackMessage(t('panel.posting.copy.id.success'));
            }}
            data-panel='posting-copyId-mobile'
          />
        </InfoItem>
        {status === 'DRAFT' && (
          <InfoItem data-panel='posting-created-container'>
            <span data-panel='posting-created-title'>{t('postings.card.detail.created')}</span>
            <span data-panel='posting-created-date'>{dates.createDate || '-'}</span>
          </InfoItem>
        )}
        {isFinished && (
          <InfoItem data-panel='posting-finished-container'>
            <span data-panel='posting-finished-title'>{t('panel.posting.status.OFFLINE')}</span>
            <span data-panel='posting-finished-date'>{dates.endDate || '-'}</span>
          </InfoItem>
        )}
        {withoutCredit && (
          <InfoItem data-panel='posting-withoutCredit-container'>
            <span data-panel='posting-withoutCredit-title'>{t('panel.status.ONLINE.endDate')}</span>
            <span data-panel='posting-withoutCredit-date'>{dates.endDate || '-'}</span>
          </InfoItem>
        )}
        {status === 'ONLINE' && (
          <>
            <InfoItem data-panel='posting-beginDate-container'>
              <span data-panel='posting-beginDate-title'>{t('panel.status.ONLINE.beginDate')}</span>
              <span data-panel='posting-beginDate-date'>{dates.beginDate || '-'}</span>
            </InfoItem>
            <InfoItem data-panel='posting-endDate-container'>
              <span data-panel='posting-endDate-title'>{t('panel.status.ONLINE.endDate')}</span>
              <span data-panel='posting-endDate-date'>{dates.endDate || '-'}</span>
            </InfoItem>
          </>
        )}
        {status === 'DELETED' && (
          <InfoItem data-panel='posting-deleted-container'>
            <span data-panel='posting-deleted-title'>{t('panel.posting.status.DELETED')}</span>
            <span data-panel='posting-deleted-date'>{dates.finishedDate || '-'}</span>
          </InfoItem>
        )}
        <InfoItem data-panel='posting-plan-container'>
          <span data-panel='posting-plan-title'>{planName && t('panel.posting.plan')}</span>
          <span data-panel='posting-plan' className='overflow-handler'>
            {isTransitionOrBundleCredit ? publicationName : planName}
          </span>
          {planName && !isPortalWithoutEcommerce && !isAssistant && (
            <UpgradePlanLink
              disabled={disablePlanLink}
              href={isParticular && canUpgradePlan && !isOffline ? linkUpgradePlan : null}
              onClick={() => {
                sendTrackingInfo('Panel', 'Mis avisos', 'Card del aviso-Mejora tu plan');
                if (!isParticular && !disablePlanLink) {
                  return setUpgradePlanModal(true);
                }
              }}
            >
              <span data-panel='posting-plan-CTA'>{t('panel.avisos.posting.CTA.improveExposition')}</span>
              <UpgradePlanIcon disabled={disablePlanLink} />
            </UpgradePlanLink>
          )}
        </InfoItem>
      </PostingInfo>
    </ContentLayout>
  );
}

const ContentLayout = styled.div`
  border-bottom: 1px solid ${selectColor('grayMediumLight')};
  font-size: 16px;
  padding: 20px 0;
  margin: 0 16px;
  .performance-container {
    position: relative;
    height: 26px;
    width: 100%;
    margin-bottom: 14px;
  }
`;

const PerformanceColors = {
  ALTO: { background: 'positiveBack', color: 'snackGreen', colorAlt: 'snackGreen', icon: PerformanceGreen },
  MEDIO: { background: 'lightYellow', color: 'brownYellow', colorAlt: 'brownYellow', icon: PerformanceYellow },
  BAJO: { background: 'redBack', color: 'negative', colorAlt: 'negative', icon: PerformanceRed },
  'NO-DATA-AVAILABLE': {
    background: 'lightGray',
    color: 'midDarkGrayAux',
    colorAlt: 'grayDarkLight',
    icon: PerformanceGray
  }
};

const PerformanceStatus = styled.div`
  width: 100%;
  min-height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  > span {
    display: inline-block;
    background-image: ${({ status }) => status && `url(${PerformanceColors[status].icon})`};
    background-repeat: no-repeat;
    background-size: contain;
    width: 20px;
    height: 21px;
    margin-right: 10px;
  }
  > div {
    font-family: Hind;
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    color: ${({ status }) => status && select(PerformanceColors[status].color)};
  }
`;

const PerformanceLink = styled.a`
  text-decoration: none;
  background-color: ${({ status }) => status && select(PerformanceColors[status].background)};
  border-radius: 5px;
  width: 100%;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 32px;
  margin-bottom: 19px;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.18);
  overflow: hidden;
`;

const ContentWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  .buttonText {
    font-family: Hind;
    font-size: 13px;
    font-style: normal;
    font-weight: 500;
    line-height: 19px;
    color: ${({ status, disabled }) =>
      status && select(!disabled ? PerformanceColors[status].colorAlt : 'grayMediumLight')};
    z-index: 1;
  }
`;

const DaysLeftBar = styled.div`
  width: ${({ fillValue }) => `${fillValue}%` || '0%'};
  height: 100%;
  background-color: ${select('grayMedium')};
  position: absolute;
  left: 0;
  z-index: 0;
`;

const PostingInfo = styled.div`
  margin-top: 29px;
`;

const InfoItem = styled.div`
  margin-bottom: 16px;
  :last-of-type {
    margin-bottom: 0;
    display: flex;
    align-items: center;
  }
  > span {
    font-family: Hind;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 26px;
    margin-right: 13px;
    :first-of-type {
      margin-right: 8px;
      color: ${select('darkGray')};
    }
    :nth-of-type(3) {
      position: relative;
      top: 5px;
    }
    &.overflow-handler {
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }
  }
`;

const UpgradePlanLink = styled.a`
  text-decoration: none;
  height: 32px;
  min-width: 155px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${select('lightGray')};
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.18);
  color: ${({ disabled }) => (disabled ? select('midLightGray') : 'black')};
  font-size: 12px;
  margin-left: auto;
`;

const UpgradePlanIcon = styled.span`
  width: 14.7px;
  height: 10px;
  background-image: ${({ disabled }) => `url(${!disabled ? trendingUp : trendingUpDisabled})`};
  background-position: center;
  background-size: cover;
  margin-right: 4.6px;
`;

const TooltipComponent = styled.div`
  white-space: pre-wrap;
  padding: 4px 0;
`;

export default MetricsSection;
